import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { X, Cookie, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'servicare_cookie_consent';

export const CookieBanner = () => {
  const { t, tObj, getTranslation } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      functional: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const consent = {
      necessary: true,
      analytics: false,
      functional: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      necessary: true, // Always true
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  const categories = tObj('cookies.categories');

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center p-4 sm:p-6 pointer-events-none">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-sm pointer-events-auto"
        onClick={() => {}} // Prevent closing by clicking backdrop
      />
      
      {/* Banner */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl pointer-events-auto animate-in slide-in-from-bottom-8 duration-500">
        {/* Header */}
        <div className="p-5 pb-0">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-100 rounded-xl shrink-0">
              <Cookie className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-900">
                {t('cookies.banner.title')}
              </h2>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                {t('cookies.banner.description')}
              </p>
            </div>
          </div>
        </div>

        {/* Expandable Details */}
        {showDetails && (
          <div className="px-5 py-4 space-y-3 border-t border-slate-100 mt-4">
            {/* Necessary Cookies */}
            <div className="flex items-start justify-between gap-4 p-3 bg-slate-50 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-slate-900 text-sm">
                    {getTranslation(categories?.necessary?.title)}
                  </span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    {t('common.buttons.confirm') === 'Confirm' ? 'Required' : 'Richiesti'}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {getTranslation(categories?.necessary?.description)}
                </p>
              </div>
              <Switch checked={true} disabled className="opacity-50" />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between gap-4 p-3 bg-slate-50 rounded-lg">
              <div className="flex-1">
                <span className="font-medium text-slate-900 text-sm">
                  {getTranslation(categories?.analytics?.title)}
                </span>
                <p className="text-xs text-slate-500 mt-1">
                  {getTranslation(categories?.analytics?.description)}
                </p>
              </div>
              <Switch 
                checked={preferences.analytics}
                onCheckedChange={(checked) => setPreferences(p => ({ ...p, analytics: checked }))}
              />
            </div>

            {/* Functional Cookies */}
            <div className="flex items-start justify-between gap-4 p-3 bg-slate-50 rounded-lg">
              <div className="flex-1">
                <span className="font-medium text-slate-900 text-sm">
                  {getTranslation(categories?.functional?.title)}
                </span>
                <p className="text-xs text-slate-500 mt-1">
                  {getTranslation(categories?.functional?.description)}
                </p>
              </div>
              <Switch 
                checked={preferences.functional}
                onCheckedChange={(checked) => setPreferences(p => ({ ...p, functional: checked }))}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="p-5 pt-4 border-t border-slate-100 mt-2">
          <div className="flex flex-col sm:flex-row gap-3">
            {!showDetails ? (
              <>
                <Button 
                  variant="outline" 
                  className="flex-1 order-3 sm:order-1"
                  onClick={handleRejectAll}
                >
                  {t('cookies.banner.rejectAll')}
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 order-2"
                  onClick={() => setShowDetails(true)}
                >
                  {t('cookies.banner.customize')}
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
                <Button 
                  className="flex-1 bg-blue-700 hover:bg-blue-800 order-1 sm:order-3"
                  onClick={handleAcceptAll}
                >
                  {t('cookies.banner.acceptAll')}
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowDetails(false)}
                >
                  <ChevronUp className="w-4 h-4 mr-1" />
                  {t('common.buttons.back')}
                </Button>
                <Button 
                  className="flex-1 bg-blue-700 hover:bg-blue-800"
                  onClick={handleSavePreferences}
                >
                  {t('cookies.banner.save')}
                </Button>
              </>
            )}
          </div>
          
          {/* Privacy Link */}
          <p className="text-center text-xs text-slate-500 mt-4">
            <Link to="/privacy" className="text-blue-600 hover:underline">
              {t('cookies.learnMore')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

// Helper to check consent
export const getCookieConsent = () => {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (consent) {
    return JSON.parse(consent);
  }
  return null;
};

export default CookieBanner;
